import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { z } from "zod";

import { ApiError, authApi } from "../lib/api";
import { describePasswordPolicy } from "../lib/passwordPolicy";

const searchSchema = z.object({ token: z.string().default("") });
export const Route = createFileRoute("/password-reset")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PasswordResetPage,
});

function PasswordResetPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    try {
      await authApi.confirmPasswordReset(token, password);
      navigate({ to: "/login" });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not reset password.");
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-5 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-6 shadow-sm"
      >
        <header>
          <h1 className="text-2xl font-semibold">Choose a new password</h1>
          <p className="text-sm opacity-60">All existing sessions will be signed out.</p>
        </header>
        <label className="block text-sm">
          <span className="mb-1 block">New password</span>
          <input
            className="form-input w-full"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="mt-1 block text-xs opacity-60">{describePasswordPolicy()}</span>
        </label>
        <label className="block text-sm">
          <span className="mb-1 block">Confirm password</span>
          <input
            className="form-input w-full"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>
        {error && (
          <div role="alert" className="text-sm text-red-600">
            {error}
          </div>
        )}
        <button
          type="submit"
          className="min-h-11 w-full rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white"
        >
          Update password
        </button>
        <Link to="/login" className="block text-center text-sm text-[var(--accent)]">
          Back to sign in
        </Link>
      </form>
    </div>
  );
}
